import type { ReactNode } from 'react';
import { LinkedText } from './links';

// Headings, bullets, tables and paragraphs: the little markdown the mobile app reads in reports and
// documents. Links open and bold stays bold (LinkedText); everything else is text. Built from
// elements, never from HTML.

/** The cells of a table row: `| a | b |` → ["a", "b"]. */
const cells = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

/** `|---|:---:|---:|`: the line under a table's header. */
const isRule = (line: string) => /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test(line.trim());

/** Amounts line up on the right: a column whose cells end in a digit, €, $ or %. */
const numeric = (s: string) => /[\d€$%]$/.test(s);

function Table({ rows }: { rows: string[][] }) {
  const [head, ...body] = rows;
  const columns = Math.max(...rows.map((r) => r.length));
  const right = Array.from({ length: columns }, (_, i) => body.length > 0 && body.every((r) => !r[i] || numeric(r[i])));
  const cell = (text: string | undefined, i: number, header: boolean) => {
    const props = { key: i, className: right[i] ? 'iv-md-num' : undefined };
    return header ? (
      <th {...props}>
        <LinkedText text={text ?? ''} />
      </th>
    ) : (
      <td {...props}>
        <LinkedText text={text ?? ''} />
      </td>
    );
  };
  return (
    <div className="iv-md-table-wrap">
      <table className="iv-md-table body-medium">
        <thead>
          <tr>{Array.from({ length: columns }, (_, i) => cell(head?.[i], i, true))}</tr>
        </thead>
        <tbody>
          {body.map((r, j) => (
            <tr key={j}>{Array.from({ length: columns }, (_, i) => cell(r[i], i, false))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MiniMarkdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let table: string[][] = [];
  const flushTable = () => {
    if (table.length === 0) return;
    const rows = table;
    table = [];
    blocks.push(<Table key={blocks.length} rows={rows} />);
  };
  const flush = () => {
    flushTable();
    if (bullets.length === 0) return;
    const items = bullets;
    bullets = [];
    blocks.push(
      <ul key={blocks.length} className="iv-md-list">
        {items.map((b, i) => (
          <li key={i} className="body-medium">
            <LinkedText text={b} />
          </li>
        ))}
      </ul>,
    );
  };

  for (const raw of text.split('\n')) {
    const line = raw.trimEnd();
    if (line.trimStart().startsWith('|')) {
      if (!isRule(line)) table.push(cells(line));
      continue;
    }
    flushTable();
    if (line.startsWith('- ') || line.startsWith('* ')) {
      bullets.push(line.slice(2));
      continue;
    }
    flush();
    const key = blocks.length;
    if (line.length === 0) {
      blocks.push(<div key={key} className="iv-md-gap" aria-hidden />);
    } else if (line.startsWith('#')) {
      const level = line.search(/[^#]/);
      const heading = level < 0 ? '' : line.slice(level).trim();
      if (!heading) continue;
      blocks.push(
        level <= 1 ? (
          <h3 key={key} className="title-large iv-md-heading">
            {heading}
          </h3>
        ) : (
          <h4 key={key} className="title-medium iv-md-heading">
            {heading}
          </h4>
        ),
      );
    } else {
      blocks.push(
        <p key={key} className="body-medium">
          <LinkedText text={line} />
        </p>,
      );
    }
  }
  flush();
  return <div className="iv-md">{blocks}</div>;
}
